import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SidebarTrigger, useSidebar } from '@/components/ui/sidebar'
import { SearchGlobal } from './SearchGlobal'
import { SearchRevealHandle } from '@/components/search/SearchRevealHandle'
import { UserHeaderMenu } from './UserHeaderMenu'
import { NotificationCenter } from './NotificationCenter'

// Route to page title mapping
const routeTitles: Record<string, string> = {
    '/': 'nav.home',
    '/dashboard': 'nav.dashboard',
    '/sessions': 'nav.sessions',
    '/billing': 'nav.billing',
    '/quotes': 'nav.commercial',
    // EPIC 11 SALE — sous-nav Commercial : routes /sales/* → titre parent (DS §13.4)
    '/sales/quotes': 'nav.commercial',
    '/sales/discount-approvals': 'nav.commercial',
    '/sales/pilotage': 'nav.commercial',
    '/sales/commissions': 'nav.commercial',
    '/sales/pending-requests': 'nav.commercial',
    '/sales/waitlists': 'nav.commercial',
    '/sales/planning-requests': 'nav.commercial',
    '/sales/renewals': 'nav.commercial',
    // EPIC 40 TND — même piège que 43_CHK ci-dessous : sans cette ligne le
    // bandeau retombe SILENCIEUSEMENT sur « Accueil » (`return t('nav.home')`
    // en fin de `getPageTitle`). L'écran s'affiche, rien n'échoue, et
    // l'en-tête ment — c'est la fiche `/sales/tenders/:id` qui le révèle,
    // parce que la boucle de repli y remonte jusqu'à `/sales/tenders`.
    '/sales/tenders': 'nav.commercial',
    /*
     * ⚠️ Les enfants portent le libellé de leur SECTION, pas le leur : le titre
     * de la barre dit où l'on est dans le produit, pas ce qu'on regarde — la
     * page le dit déjà. Un contrôle du dépôt vérifie les deux sens (aucune
     * route de navigation absente d'ici, et aucun libellé d'enfant spécifique).
     */
    '/sales/tenders/watch': 'nav.commercial',
    '/sales/tenders/library': 'nav.commercial',
    '/sales/tenders/settings': 'nav.commercial',
    '/clients': 'nav.clients',
    // TND-013 — la vue transverse « Révisions de prix », sous-entrée de Clients.
    '/clients/price-revisions': 'nav.clients',
    '/learners': 'nav.learners',
    '/directory': 'nav.directory',
    // EPIC 43_CHK — sans cette ligne, le bandeau retombe SILENCIEUSEMENT sur
    // « Accueil » (`return t('nav.home')` en fin de `getPageTitle`) : la page
    // s'affiche, rien n'échoue, et l'en-tête ment. Toute route neuve passe ici.
    //
    // ⚠️ Le libellé est celui du PARENT — « Suivi & automatisation » — et non
    // « Checklists » : ces deux routes sont les ENFANTS d'une section
    // repliable, et §13.4 veut que le bandeau nomme la section pendant que la
    // page porte son propre sous-titre. Écrire le libellé de l'enfant fait
    // changer le bandeau en naviguant DANS une section, ce qui se lit comme un
    // changement de module.
    '/checklists': 'nav.tracking',
    '/automation/workflows': 'nav.tracking',
    '/settings/workflows': 'nav.workflows',
    '/notifications': 'notifications.title',
    '/products': 'nav.products',
    '/instructors': 'nav.instructors',
    // Routes ENFANTS de la section : elles reprennent le titre du PARENT dans
    // l'en-tête (DESIGN_SYSTEM §13.4) — le sous-titre propre à l'écran est
    // porté par la page elle-même, pas par le bandeau.
    '/instructors/requests': 'nav.instructors',
    '/instructors/requirements': 'nav.instructors',
    '/instructors/coverage': 'nav.instructors',
    /*
     * ⚠️ **Ces deux-là sont sous `/settings/` et n'appartiennent PAS aux
     * Paramètres.** Le référentiel du matériel et les types de contrôle se
     * règlent depuis la logistique (arbitrage 2026-09-01) ; seule leur URL est
     * restée sous `/settings`, pour ne pas casser les liens existants. Sans ces
     * deux lignes, la boucle de repli remonte à `/settings` et le bandeau
     * annonce « Paramètres » à quelqu'un qui vient du parc — l'en-tête ment sur
     * l'endroit où l'on est, ce qui est sa seule fonction.
     *
     * Elles précèdent `/settings` : la boucle s'arrête au PREMIER segment
     * connu, en partant de la racine — mais `routeTitles[pathname]` est testé
     * d'abord, en chemin COMPLET, donc l'ordre littéral n'a pas d'importance
     * ici. On les groupe quand même, pour qu'on les trouve.
     */
    '/settings/logistics/control-types': 'nav.logistics',
    '/settings/logistics/resource-referentiel': 'nav.logistics',
    '/settings': 'nav.settings',
    '/changelog': 'changelog.title',
    '/knowledge': 'nav.help',
    '/catalog': 'nav.catalog',
    '/catalog/formations': 'nav.catalog',
    '/catalog/prestations': 'nav.catalog',
    '/catalog/articles': 'nav.catalog',
    '/catalog/frais': 'nav.catalog',
    '/pedagogy': 'nav.pedagogy',
    '/pedagogy/assessments': 'nav.pedagogy',
    '/pedagogy/elearning': 'nav.pedagogy',
    '/pedagogy/referentials': 'nav.pedagogy',
    '/quality': 'nav.quality',
    '/quality/non-conformities': 'nav.quality',
    '/quality/action-plan': 'nav.quality',
    '/quality/qualiopi': 'nav.quality',
    '/quality/satisfaction': 'nav.quality',
    '/quality/monitoring': 'nav.quality',
    '/logistics/locations': 'nav.logistics',
    '/logistics/catering': 'nav.logistics',
    '/logistics/accommodation': 'nav.logistics',
    '/logistics/resources': 'nav.logistics',
    '/account/profile': 'profile.title',
    '/account/appearance': 'appearance.title',
    '/account/notifications': 'account.notifications.title',
    '/account/security': 'user.security',
}

interface AppHeaderProps {
    enableAssistant: boolean
    userId: string
}

/**
 * Modern App Header with floating islands design.
 * No border, just floating search and user islands with shadows.
 */
export function AppHeader({ enableAssistant, userId }: AppHeaderProps) {
    const { t } = useTranslation()
    const location = useLocation()
    const { isMobile } = useSidebar()

    // Get page title based on current route
    const getPageTitle = (): string => {
        if (routeTitles[location.pathname]) {
            return t(routeTitles[location.pathname])
        }
        const pathParts = location.pathname.split('/').filter(Boolean)
        let currentPath = ''
        for (const part of pathParts) {
            currentPath += `/${part}`
            if (routeTitles[currentPath]) {
                return t(routeTitles[currentPath])
            }
        }
        return t('nav.home')
    }

    return (
        <header className="flex shrink-0 items-center justify-between gap-2 px-4 md:px-6 pt-[calc(0.75rem+env(safe-area-inset-top))] pb-3">
            {/* Left: Mobile trigger + Page Title (priorité de troncature) */}
            <div className="flex min-w-0 shrink items-center gap-2">
                {isMobile && <SidebarTrigger className="shrink-0" />}
                <h1 className="truncate text-lg font-semibold tracking-tight text-foreground md:text-2xl md:font-bold">
                    {getPageTitle()}
                </h1>
            </div>

            {/* Mobile: poignée de révélation (chevron oscillant) centrée dans la
                bande haute. Tap → ouvre ; swipe-down sur le header → ouvre aussi.
                Plus de champ persistant sur mobile (récup. d'espace vertical). */}
            <div className="flex flex-1 justify-center md:hidden">
                <SearchRevealHandle />
            </div>

            {/* Desktop: îlot central = VRAI champ fusionné (panneau ancré dessous).

                ⚠️ **`min-w-0` — sans lui l'en-tête DÉBORDE entre 768 et ~890 px.**
                Un enfant de flex a `min-width: auto` : il ne descend jamais sous la
                min-content de son contenu, ici un champ de recherche avec son
                libellé. Or `md:` est précisément l'instant où la barre latérale prend
                256 px : à 768 px de fenêtre il reste 464 px d'en-tête pour un titre,
                cet îlot et la pilule avatar+cloche (273 px, `shrink-0` à juste
                titre). L'îlot refusait sa part, et la pilule sortait de l'écran —
                document mesuré à 855 px pour une fenêtre de 768, sur TOUS les écrans
                du produit, pas seulement celui où le défaut a été vu.
                Relevé conservé dans `tests/mobile/bande-md.spec.ts`. */}
            <div className="mx-4 hidden min-w-0 max-w-lg flex-1 justify-center md:flex">
                <div className="w-full rounded-xl border border-border bg-card shadow-md">
                    <SearchGlobal className="w-full" enableAssistant={enableAssistant} userId={userId} />
                </div>
            </div>

            {/* Right: User + Notifications Island (loupe doublon supprimée). */}
            <div className="flex shrink-0 items-center gap-1 rounded-xl border border-border bg-card px-2 py-1.5 shadow-md">
                <UserHeaderMenu />
                <div className="mx-1 h-6 w-px bg-border" />
                <NotificationCenter />
            </div>
        </header>
    )
}
